/**
 * Tiny synthesized SFX (oscillator + gain envelopes). No asset files.
 *
 * Mute-safe: missing `AudioContext`, construction failure, or a blocked
 * context all become no-ops. Call `resumeAudio()` once from a user-gesture
 * path (keydown / pointerdown) so the browser unlocks playback.
 */

type OscType = OscillatorType

interface Blip {
  /** Start frequency in Hz. */
  freq: number
  /** Optional slide target; defaults to `freq`. */
  freqEnd?: number
  /** Duration in seconds. */
  dur: number
  type?: OscType
  /** Peak gain (linear). Keep small — these stack. */
  gain?: number
  /** Delay before the note starts, in seconds. */
  when?: number
}

let ctx: AudioContext | null | undefined
let master: GainNode | undefined
let resuming = false
let muted = false
try {
  muted = globalThis.localStorage?.getItem('tavi:muted') === 'true'
} catch {
  // Storage can be unavailable; the control still works for this session.
}

export function isAudioMuted(): boolean {
  return muted
}

export function setAudioMuted(value: boolean): void {
  muted = value
  if (master !== undefined && ctx != null) {
    master.gain.setValueAtTime(muted ? 0 : 1, ctx.currentTime)
  }
  try {
    globalThis.localStorage?.setItem('tavi:muted', String(muted))
  } catch {
    // Keep the in-memory preference when storage is denied.
  }
}

function audioContextCtor(): (new () => AudioContext) | null {
  if (typeof globalThis === 'undefined') return null
  const g = globalThis as {
    AudioContext?: new () => AudioContext
    webkitAudioContext?: new () => AudioContext
  }
  return g.AudioContext ?? g.webkitAudioContext ?? null
}

function getCtx(): AudioContext | null {
  if (ctx !== undefined) return ctx
  const Ctor = audioContextCtor()
  if (Ctor === null) {
    ctx = null
    return null
  }
  try {
    ctx = new Ctor()
  } catch {
    ctx = null
  }
  return ctx
}

/** Unlock / resume the shared context after a user gesture. Safe to call often. */
export function resumeAudio(): void {
  if (muted) return
  const c = getCtx()
  if (c === null || c.state !== 'suspended' || resuming) return
  resuming = true
  try {
    void c
      .resume()
      .catch(() => {})
      .finally(() => {
        resuming = false
      })
  } catch {
    resuming = false
  }
}

function blip(spec: Blip): void {
  // Never queue sounds while locked: unlocking later must not replay old events.
  if (muted) return
  const c = getCtx()
  if (c === null || c.state !== 'running') return

  const now = c.currentTime + (spec.when ?? 0)
  const dur = Math.max(0.01, spec.dur)
  const peak = spec.gain ?? 0.07
  const freqEnd = spec.freqEnd ?? spec.freq

  try {
    const osc = c.createOscillator()
    const gain = c.createGain()
    osc.type = spec.type ?? 'square'
    osc.frequency.setValueAtTime(spec.freq, now)
    if (freqEnd !== spec.freq) {
      osc.frequency.exponentialRampToValueAtTime(
        Math.max(1, freqEnd),
        now + dur,
      )
    }
    gain.gain.setValueAtTime(0.0001, now)
    gain.gain.exponentialRampToValueAtTime(peak, now + 0.01)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + dur)
    osc.connect(gain)
    if (master === undefined) {
      master = c.createGain()
      master.gain.setValueAtTime(muted ? 0 : 1, c.currentTime)
      master.connect(c.destination)
    }
    gain.connect(master)
    osc.onended = () => {
      osc.disconnect()
      gain.disconnect()
    }
    osc.start(now)
    osc.stop(now + dur + 0.02)
  } catch {
    // Autoplay / closed context / odd host — stay silent.
  }
}

function chord(notes: Blip[]): void {
  for (const n of notes) blip(n)
}

export function sfxJump(): void {
  blip({ freq: 380, freqEnd: 640, dur: 0.09, type: 'square', gain: 0.06 })
}

export function sfxCoin(): void {
  chord([
    { freq: 880, dur: 0.06, type: 'square', gain: 0.05 },
    { freq: 1175, dur: 0.1, type: 'square', gain: 0.05, when: 0.05 },
  ])
}

export function sfxStomp(): void {
  blip({ freq: 220, freqEnd: 90, dur: 0.1, type: 'triangle', gain: 0.08 })
}

export function sfxHurt(): void {
  blip({ freq: 320, freqEnd: 140, dur: 0.14, type: 'sawtooth', gain: 0.07 })
}

export function sfxDeath(): void {
  chord([
    { freq: 300, freqEnd: 120, dur: 0.18, type: 'sawtooth', gain: 0.08 },
    {
      freq: 180,
      freqEnd: 60,
      dur: 0.28,
      type: 'triangle',
      gain: 0.06,
      when: 0.1,
    },
  ])
}

export function sfxCheckpoint(): void {
  chord([
    { freq: 523, dur: 0.08, type: 'square', gain: 0.05 },
    { freq: 659, dur: 0.08, type: 'square', gain: 0.05, when: 0.07 },
    { freq: 784, dur: 0.14, type: 'square', gain: 0.06, when: 0.14 },
  ])
}

export function sfxWin(): void {
  chord([
    { freq: 523, dur: 0.1, type: 'square', gain: 0.06 },
    { freq: 659, dur: 0.1, type: 'square', gain: 0.06, when: 0.1 },
    { freq: 784, dur: 0.1, type: 'square', gain: 0.06, when: 0.2 },
    { freq: 1047, dur: 0.22, type: 'square', gain: 0.07, when: 0.3 },
  ])
}

export function sfxPower(): void {
  chord([
    { freq: 392, dur: 0.07, type: 'square', gain: 0.05 },
    { freq: 523, dur: 0.07, type: 'square', gain: 0.05, when: 0.06 },
    { freq: 659, dur: 0.07, type: 'square', gain: 0.05, when: 0.12 },
    { freq: 784, dur: 0.16, type: 'square', gain: 0.06, when: 0.18 },
  ])
}

/** Test helper: forget a cached context so the next call re-probes the host. */
export function resetAudioForTests(): void {
  ctx = undefined
  master = undefined
  resuming = false
  muted = false
}
